import React from 'react';

import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import {
  Dropdown,
  DropdownButton,
  DropdownDescription,
  DropdownItem,
  DropdownLabel,
  DropdownMenu
} from '@oysterjs/ui/dropdown';

export type RowAction = {
  label: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  disabled?: boolean;
  /** A line under the label, such as why the action is disabled. */
  description?: string;
} & (
  | { onSelect: () => void; href?: never; target?: never }
  | { href: string; target?: string; onSelect?: never }
);

/**
 * The vertical three-dot menu that holds a table row's actions. `label` names
 * the row for screen readers, since the button itself is only an icon.
 */
export const RowActionsMenu = ({
  label,
  actions,
  disabled
}: {
  label: string;
  actions: RowAction[];
  disabled?: boolean;
}) => (
  <Dropdown>
    <DropdownButton plain aria-label={label} disabled={disabled}>
      <EllipsisVerticalIcon className="size-5" />
    </DropdownButton>
    <DropdownMenu anchor="bottom end">
      {actions.map((action) =>
        action.href ? (
          <DropdownItem
            key={action.label}
            href={action.href}
            target={action.target}
            rel={action.target === '_blank' ? 'noreferrer' : undefined}
            disabled={action.disabled}
          >
            {action.icon && <action.icon data-slot="icon" />}
            <DropdownLabel>{action.label}</DropdownLabel>
            {action.description && <DropdownDescription>{action.description}</DropdownDescription>}
          </DropdownItem>
        ) : (
          <DropdownItem key={action.label} onClick={action.onSelect} disabled={action.disabled}>
            {action.icon && <action.icon data-slot="icon" />}
            <DropdownLabel>{action.label}</DropdownLabel>
            {action.description && <DropdownDescription>{action.description}</DropdownDescription>}
          </DropdownItem>
        )
      )}
    </DropdownMenu>
  </Dropdown>
);
